import head from 'lodash/head';
import last from 'lodash/last';
import isNil from 'lodash/isNil';

import {
  DateKeyedFunnelSegments,
  FunnelOrder,
  FunnelSegment,
  FunnelStep,
  SelectMode,
} from 'common/report/models/funnels/types';
import {OVERALL_PROP, SEGMENT_SEPARATOR} from 'common/report/models/funnels/constants';
import {Placement} from 'common/components/tooltip/types';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {getSelectedSegments} from 'common/report/util/funnels/utils';
import {AbsoluteDate} from 'common/widgets/date-range-picker/models/types';
import {formatDate, parseDate, transformDateByRelativeDate} from 'common/util/date';
import {isGroupedEntry} from 'common/widgets/query-entry-section/types';
import {SavedBookmark} from 'common/report/queries/app-api/bookmark-client';
import {FunnelQueryBookmark} from 'common/report/models/funnels/types';
import {BarStyle, SelectedSegmentStep} from 'common/report/models/funnels/bar-chart/types';
import {getFunnelStep, getMedian} from 'common/report/models/funnels/bar-chart/utils';
import {FunnelParamsForFlows} from 'common/report/models/flows/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {MAX_SELECTED_SEGMENTS_LIMIT, ReportName} from 'common/report/constants';

import {FunnelsQuery} from './funnels-query';

/**
 * helper function to update TopPathRenameMap
 * eventually we should move FunnelsQuery into common and avoid duplicated functions
 */
function _addToTopPathsRenameMap(renameMap: {[key: string]: string}, segment: FunnelSegment, labels: string[]) {
  if (last(segment.props).prop !== `undefined` && ![`$other`, OVERALL_PROP].includes(head(segment.props).prop)) {
    renameMap[segment.prop] = segment.props
      .slice(0, -1)
      .map((item) => item.propName)
      .concat(
        last(segment.props)
          .prop.split(`,`)
          .map((stepIdx) => labels[stepIdx] || stepIdx)
          .join(` ➞ `),
      )
      .join(SEGMENT_SEPARATOR);
  }
}

/**
 * Computes mapping from segment names in the data to user friendly names.
 * Currently, it returns the mapping for converting any order segments
 * from step indices to step names.
 */
export function getTopPathsRenameMap(
  funnelsQuery: FunnelsQuery,
  funnelSegments: DateKeyedFunnelSegments[],
): {[key: string]: string} {
  const renameMap: {[key: string]: string} = {};
  if (funnelsQuery.isAnyOrderFunnel() && funnelsQuery.isSegmentedByTopPaths()) {
    const labels = funnelsQuery.getStepLabels(true);
    for (const currentDate of funnelSegments || []) {
      for (const segment of currentDate[`segments`]) {
        _addToTopPathsRenameMap(renameMap, segment, labels);
      }
    }
  }
  return renameMap;
}

/** Apply top-paths rename map to all segments */
export function getTopPathRenamedSegments(
  funnelsQuery: FunnelsQuery,
  segmentData: DateKeyedFunnelSegments[],
): FunnelSegment[] {
  const segmentRenameMap = getTopPathsRenameMap(funnelsQuery, segmentData);
  const segments = segmentData?.[0]?.segments;
  return Object.values(segments).map((item) => {
    return item.prop in segmentRenameMap ? {...item, propName: segmentRenameMap[item.prop]} : item;
  });
}

/** helper function to get conversion count on last step */
function _getNumericFunnelConversion(steps) {
  const lastStep = steps[steps.length - 1];
  return lastStep.count;
}

/**
 * Get all data segments, transformed by the top-paths rename and ordered by conversions
 */
export function getRenamedOrderedSegmentSteps(
  funnelsQuery: FunnelsQuery,
  segmentData: DateKeyedFunnelSegments[],
  comparatorOverride?: (a: FunnelSegment, b: FunnelSegment) => number,
) {
  return getTopPathRenamedSegments(funnelsQuery, segmentData).sort(
    comparatorOverride ||
      ((a, b) => {
        if (a.prop === OVERALL_PROP) {
          return -1;
        } else if (b.prop === OVERALL_PROP) {
          return 1;
        } else {
          return _getNumericFunnelConversion(b.steps) - _getNumericFunnelConversion(a.steps);
        }
      }),
  );
}

export function getMedianComparator(
  stepIndex: number,
  medianAccessor: keyof FunnelStep,
): (a: FunnelSegment, b: FunnelSegment) => number {
  return (a: FunnelSegment, b: FunnelSegment): number => {
    if (a.prop === OVERALL_PROP) {
      return -1;
    } else if (b.prop === OVERALL_PROP) {
      return 1;
    } else {
      return (
        getMedian(getFunnelStep(b.steps, stepIndex), medianAccessor) -
        getMedian(getFunnelStep(a.steps, stepIndex), medianAccessor)
      );
    }
  };
}

export function getLegendSections(
  selected: ColorChoice[],
  selectMode: SelectMode,
  funnelsQuery: FunnelsQuery,
  segmentData: DateKeyedFunnelSegments[],
): Section[] {
  const segments = getRenamedOrderedSegmentSteps(funnelsQuery, segmentData);
  if (segments.length === 1) {
    return null;
  }

  const selectedSegments = getSelectedSegments(selected, selectMode, segments);
  const isAtMaxSelections = selectedSegments.length >= MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels];
  const keyToColorMap = new Map(
    selectedSegments.map<[string, string]>((s) => [s.key, s.color]),
  );
  const items: SectionItem[] = segments.map((segment) => {
    const isSelected = keyToColorMap.has(segment.prop);
    const color = keyToColorMap.get(segment.prop);
    const isDisabled = !isSelected && isAtMaxSelections;
    return {
      color,
      label: segment.propName,
      value: segment.prop,
      isDisabled,
      isSelected,
      wrapLabel: true,
      tooltip: isDisabled
        ? {
            text: `Cannot select more than ${MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels]} items`,
            placement: Placement.Left,
          }
        : null,
    };
  });
  return [{items}];
}

function _formatTime(from: AbsoluteDate | Date, to: AbsoluteDate | Date): string {
  const customFormatting = {day: `MMM D`};
  if (!(from instanceof Date)) {
    from = parseDate(from);
  }

  if (!(to instanceof Date)) {
    to = parseDate(to);
  }
  return `${formatDate(from, {unit: `day`, customFormatting})} - ${formatDate(to)}`;
}

export function getCalendarDateLabelsFromDates(
  dateRange: DateRange | Partial<FullLengthDateRange>,
  comparisonFrom: Date,
  projectAsOfDate?: string,
): string[] {
  const comparisonTo = transformDateByRelativeDate(comparisonFrom, dateRange.getRelativeDate());
  const {from, to} = dateRange.getCalendarDatesAsOf(projectAsOfDate);
  return [_formatTime(from, to), _formatTime(comparisonFrom, comparisonTo)];
}

// TODO(sam): change FunnelsQuery to explicitly create a `DateRange` so we don't pass in a Partial<FullLengthDateRange> to the `fromDates` function
export function getCalendarDateLabels(
  funnelsQuery: FunnelsQuery,
  comparisonSegmentData: DateKeyedFunnelSegments[],
  projectAsOfDate?: string,
): string[] {
  const {dateRange} = funnelsQuery;
  const comparisonFrom = comparisonSegmentData?.[0].date;
  return getCalendarDateLabelsFromDates(dateRange, comparisonFrom, projectAsOfDate);
}

export function isQueryGroupedSteps(funnelsQuery: FunnelsQuery) {
  return funnelsQuery.events.some((event) => isGroupedEntry(event));
}

export function createBucketLineChartFunnelData({
  segmentData,
  selected,
  stepIndex,
  hasComparison = false,
  isComparisonData = false,
}: {
  segmentData: FunnelSegment[];
  selected: ColorChoice[];
  stepIndex: number | null;
  hasComparison: boolean;
  isComparisonData: boolean;
}): Map<string, SelectedSegmentStep> {
  let style: BarStyle = null;
  const selectedSegments = new Map<string, string>(selected.map((s) => [s.key, s.color]));
  const data = new Map<string, SelectedSegmentStep>();

  // if there is comparison data, we need to set style
  if (hasComparison) {
    style = isComparisonData ? BarStyle.Stripe : BarStyle.Solid;
  }

  segmentData
    .filter((segment) => selectedSegments.has(segment.prop))
    .forEach((segment) => {
      data.set(segment.prop, {
        segmentKey: segment.prop,
        segmentName: segment.propName,
        step: getFunnelStep(segment.steps, stepIndex),
        color: selectedSegments.get(segment.prop),
        ...(style ? {style} : null),
      });
    });

  return data;
}

export const DROP_OFF_DEFAULT_FORWARD_STEPS = 4;

const FLOWS_ANY_EVENT = `$any_event`;

export function addFunnelBehaviorToFlowsBookmark(
  flowsBookmark: any,
  funnelParams: FunnelParamsForFlows,
  addDefaultSteps: boolean,
): any {
  const funnelBookmark = funnelParams.funnelBookmark;
  const funnelSelectedStep = funnelParams.funnelSelectedStep;
  const propertyParams = createFunnelBehaviorProperty(funnelBookmark, funnelSelectedStep.segments);
  for (let index = 0; index <= flowsBookmark.steps.length - 1; index++) {
    const step = flowsBookmark.steps[index];
    // for now, we don't want to add forward steps to the last step
    if (addDefaultSteps && index !== flowsBookmark.steps.length - 1) {
      step[`forward`] = DROP_OFF_DEFAULT_FORWARD_STEPS;
    }
    const funnelStepIndex = funnelSelectedStep.stepIndex + index;
    step[`property_filter_params_list`] = [
      {
        filter: {operator: `==`, operand: funnelStepIndex + 1},
        property: propertyParams,
        selected_property_type: `number`,
        type: `number`,
      },
    ];
    if (funnelBookmark.funnel_order === FunnelOrder.Any) {
      // for any-order queries, remove any event selectors, including custom event IDs/names
      // the flows query instead relies on the funnel property filter to find the correct steps
      step[`event`] = FLOWS_ANY_EVENT;
      delete step.custom_event;
      delete step.custom_event_name;
    }
  }
  return flowsBookmark;
}

function createFunnelBehaviorProperty(bookmark: any, funnelSegmentSelection: string): any {
  let convertBehavior = [
    {
      convert_behavior: {step: 1},
    },
    {
      drop_off_behavior: {step: bookmark.steps.length},
    },
  ];
  const groupByValue = funnelSegmentSelection;
  if (!isNil(groupByValue)) {
    convertBehavior = convertBehavior.map((param) => {
      param[`segments`] = groupByValue.split(SEGMENT_SEPARATOR);
      return param;
    });
  }
  const behaviors = {
    bookmark,
    behavior_params: convertBehavior,
    computed_property_type: `event`,
  };
  const funnelBehaviorPropertyParams = {
    customProperty: {
      behavior: {funnel_report: behaviors},
      name: `step`,
    },
    name: `funnel_behavior_property`,
    source: `user`,
  };
  return funnelBehaviorPropertyParams;
}

export function canUseSessions() {
  return true;
}

/**
 * Return the funnels bookmark associated with a dashboard, also known as the FunnelsQueryBookmark.
 * Function returns a versioned FunnelQueryBookmark if it exists.
 *
 * Two History lessons:
 *
 * 1. Whenever we add a funnels bookmark to the dashboard, we create a copy of it specifically for its use.
 * For example, if we have a bookmark of ID 1 and we add it to the dashboard, we copy it and add its copy, ID 2
 * to dashboards. This means that in dashboards, we will always query for ID 2, and to actually display the funnel
 * in funnels we query for ID 1. This also means that if we want to get the funnel associated with the dashboard
 * in funnels, we also need to query for ID 2.
 *
 * 2. there are two versions of FunnelQueryBookmark (FQB).
 *
 * version: undefined - this was when they used to store information not in the funnels
 * bookmark; like date_range, filters, unit. These have a many to one relationship
 * with the funnels bookmark and the extra information is used at query time.
 *
 * version: 1 - these FQBs are linked to funnel bookmarks which store all the
 * information needed for the query (groupBy, filterBy), and need only contain
 * a funnel_id. These FQBs are 1-1 with the original funnel bookmark and will
 * stay in sync on the dashboard.
 *
 * This function ignores FQBs without a version, and those dashboard relationships
 * won't be reflected by a check-mark in this button. The dash cards will still
 * work, and the funnel can be re-added as a 1-1 versioned FQB.
 *
 */
export function findMatchingFunnelQueryBookmark(id, funnelQueryBookmarks) {
  if (!(funnelQueryBookmarks && funnelQueryBookmarks.length > 0)) {
    return null;
  }

  return funnelQueryBookmarks.find((bookmark: SavedBookmark<FunnelQueryBookmark>) => {
    return bookmark.params.version && bookmark.params.funnel_id === id;
  });
}
